import { v4 as uuidv4 } from 'uuid';
import {
  AppDispatch,
  LabelStyle,
  LabelType,
  addLabelInImage,
  removeLabelInImage,
  rotateLabelInImage,
  updateLabel,
  setLabelStyle,
  groupedUndo,
} from '../../../../store';
import { labelHtml, labelText } from '../../../../utils/labelText';
import { LabelStyleWithRotation } from './EditLabel';

interface SaveEditedLabelParams {
  dispatch: AppDispatch;
  marker: LabelType;
  targetLabel: LabelType;
  name: string;
  defaultLabelStyle: LabelStyle;
  defaultRotation: number;
  position: { x: number; y: number; rotation?: number; } | null;
  changes: Partial<LabelStyleWithRotation>;
  selectedLabels: LabelType[];
}

export function saveEditedLabel({
  dispatch,
  marker,
  targetLabel,
  name,
  defaultLabelStyle,
  defaultRotation,
  position,
  changes,
  selectedLabels,
}: SaveEditedLabelParams) {

  /**
    * Get the style from the label, apply the changes, and return only the
    * styles that differ from the default.
    * @param label - The label to get the style for
    * @param defaultStyle - The default style to compare against
    * @param changes - The style changes to apply
    * @returns An object containing only the style properties that differ from the default
    */
  const getStyleForLabel = (
    label:LabelType|undefined,
    defaultStyle:LabelStyle,
    changes:Partial<LabelStyle>,
  ) => {
    if (!label) return {};

    // Compute effective style by merging current style state with changes
    const effectiveStyle = { ...label.preferences?.style, ...changes };

    // Get styles that differ from defaults
    return Object.fromEntries(
      Object.entries(effectiveStyle).filter(([key, val]) => {
        return val !== defaultStyle[key as keyof LabelStyle];
      })
    );
  };

  const { rotation:rotationChange, ...styleChanges } = changes;
  const rotation = rotationChange === defaultRotation ? undefined : rotationChange;

  groupedUndo.batch(() => {
    if (marker.id === 'newLabel') {

      const { x:lat, y:lng } = position ?? {};
      let { id } = targetLabel;

      // Create a new label
      if (id === 'newLabel') {
        if (!labelText(name)) return; // Don't create empty-named labels
        id = uuidv4();
        dispatch(updateLabel({ id, name: labelHtml(name) }));
      }

      // Create new label pointing an existing label
      const style = getStyleForLabel(targetLabel, defaultLabelStyle, styleChanges);
      dispatch(setLabelStyle({ id, style }));
      dispatch(addLabelInImage({ label: id, lat, lng, rotation }));

    } else if (targetLabel.id === marker.id) { // Updating existing labels
      if (!labelText(name)) {
        dispatch(removeLabelInImage(marker.id));
        return;
      }
      dispatch(updateLabel({ id: targetLabel.id, name: labelHtml(name) }));

      // Update all selected labels
      selectedLabels.forEach((label) => {
        const style = getStyleForLabel(label, defaultLabelStyle, styleChanges);
        dispatch(setLabelStyle({ id: label.id, style }));
        if (rotationChange !== undefined) {
          dispatch(rotateLabelInImage({ id: label.id, rotation }));
        }
      });

    } else { // Update an existing label to point another label
      dispatch(setLabelStyle({ id: targetLabel.id, style: styleChanges }));
      dispatch(addLabelInImage({ label: marker.id, newLabel: targetLabel.id }));
      if (rotationChange !== undefined) {
        dispatch(rotateLabelInImage({ id: targetLabel.id, rotation }));
      }
    }
  });
}

export default saveEditedLabel;
